import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Save, AlertCircle, CheckCircle, Calendar as CalendarIcon } from 'lucide-react';
import GlassButton from '../ui/GlassButton';
import AvailabilityEditorCore, { AvailabilityRow } from './AvailabilityEditorCore';
import { bookingApi } from '../../services/api';
import './Booking.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  return fallback;
};

type PageState = 'loading' | 'error' | 'loaded';

// ─── PublicAvailabilityPage ───────────────────────────────────────────────────

const PublicAvailabilityPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  const [resourceName, setResourceName] = useState('');
  const [timezone, setTimezone] = useState('');
  // day_of_week 0=Monday … 6=Sunday — MUST match booking_engine (d.weekday()) and AvailabilityEditorCore
  const [rows, setRows] = useState<AvailabilityRow[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const load = useCallback(async () => {
    if (!token) {
      setErrorMsg('This link is no longer valid or has expired.');
      setPageState('error');
      return;
    }
    try {
      const data = await bookingApi.publicAvailabilityGet(token);
      setResourceName(data.resource_name);
      setTimezone(data.timezone);
      setRows(data.availability ?? []);
      setBlackoutDates(data.blackout_dates ?? []);
      setPageState('loaded');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 || status === 410) {
        setErrorMsg('This link is no longer valid or has expired.');
      } else {
        setErrorMsg('Failed to load your availability. Please try again.');
      }
      setPageState('error');
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!token) return;
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const updated = await bookingApi.publicAvailabilitySet(token, {
        availability: rows,
        blackout_dates: blackoutDates,
      });
      setRows(updated.availability ?? rows);
      setBlackoutDates(updated.blackout_dates ?? blackoutDates);
      setSaveSuccess('Your availability has been saved.');
    } catch (e: any) {
      setSaveError(getApiError(e, 'Failed to save availability.'));
    } finally {
      setSaving(false);
    }
  };

  // ─ Loading ─
  if (pageState === 'loading') {
    return (
      <div className="bk-page">
        <div className="bk-card">
          <p className="bk-desc" style={{ textAlign: 'center' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // ─ Error ─
  if (pageState === 'error') {
    return (
      <div className="bk-page">
        <div className="bk-card">
          <h1 className="bk-title">Link unavailable</h1>
          <p className="bk-desc">{errorMsg}</p>
        </div>
      </div>
    );
  }

  // ─ Loaded ─
  return (
    <div className="bk-page">
      <div className="bk-card">
        <h1 className="bk-title">My availability</h1>
        {resourceName && <p className="bk-subtitle">{resourceName}</p>}
        {timezone && (
          <p className="bk-meta" style={{ justifyContent: 'center' }}>
            <span className="bk-meta-item">
              <CalendarIcon size={14} /> {timezone}
            </span>
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
          <AvailabilityEditorCore
            dayLabels="long"
            rows={rows}
            onRowsChange={setRows}
            blackoutDates={blackoutDates}
            onBlackoutChange={setBlackoutDates}
          />

          {saveError && (
            <div className="fi-error" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <AlertCircle size={14} /> {saveError}
            </div>
          )}
          {saveSuccess && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--success, #16a34a)', fontSize: 'var(--text-sm)' }}>
              <CheckCircle size={14} /> {saveSuccess}
            </div>
          )}

          <GlassButton variant="primary" onClick={save} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

export default PublicAvailabilityPage;
